import AddIcon from '@mui/icons-material/Add';
import { Button, Menu, MenuItem } from '@mui/material';
import { useQueryStates } from 'nuqs';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useListViewContext } from './context/ListViewContext';
import { FilterDefinition } from './types';

/**
 * Component that provides a menu to add new filters.
 * Only shows filters that aren't already active (non-permanent filters without a value in URL).
 * Uses the composition-based configuration to determine available filters.
 */
export function AddFilterMenu({
  onAddFilter,
  activeFilterIds,
}: {
  onAddFilter?: (filterId: string) => void;
  activeFilterIds?: Set<string>;
}) {
  const { $t } = useIntl();
  const { filters, parsers, listId } = useListViewContext();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Read all filter values from URL (parsers from context are stable)
  const [filterValues] = useQueryStates(parsers);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Get all non-permanent filters
  const nonPermanentFilters = filters.filter((f) => !f.isPermanent);

  // Filter out filters that already have values or are already active (added to the bar).
  // For boolean filters with default false, value is false when "unset", so treat false as no value
  // so they appear in the add menu until the user turns them on.
  // Empty array (e.g. parseAsCommaSeparatedList.withDefault([])) is also treated as no value.
  const availableFilters = nonPermanentFilters.filter((f) => {
    const value = filterValues[f.name];
    const hasNoValue =
      value === null ||
      value === undefined ||
      value === '' ||
      value === false ||
      (Array.isArray(value) && value.length === 0);
    const notActive = !activeFilterIds?.has(f.name);
    return hasNoValue && notActive;
  });

  const handleAddFilter = (config: FilterDefinition) => {
    if (onAddFilter) {
      // Call the callback to add the filter to the active set
      onAddFilter(config.name);
    }
    handleClose();
  };

  return (
    <>
      <Button
        id={`${listId}-add-filter-btn`}
        size="small"
        variant="outlined"
        color="secondary"
        startIcon={<AddIcon />}
        onClick={handleClick}
        disabled={availableFilters.length === 0}
      >
        {$t({
          id: 'common.text.addFilter',
          defaultMessage: 'Filtro',
        })}
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {availableFilters.map((config) => (
          <MenuItem key={config.name} onClick={() => handleAddFilter(config)}>
            {config.label === false ? config.name : $t(config.label)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
